/**
 * PartnerCard.jsx
 * Shared Partner Network design. The Partner Preview page and the live website
 * both render partners with THIS component, so an approved partner looks exactly
 * like the preview they saw.
 *
 * Exports:
 *   T, CATEGORIES, SAMPLE_LOGO, useBrandFonts
 *   PartnerCard          - one partner listing
 *   PartnerGrid          - responsive grid wrapper
 *   PartnerNetworkSection - live section for pilotlinkusa.org/partners (fetches real partners)
 *
 * Live API expected (GET /api/partners/), only approved + published partners:
 *   [{ id, company_name, category, description, website, link_url, logo_url }]
 *   link_url = tracked referral URL (optional); falls back to website.
 */
import { useEffect, useState } from "react";

// ---------- Design tokens ----------
export const T = {
  navy: "#002349",
  navyDeep: "#001733",
  amber: "#F5B400", // oversize-load banner yellow
  amberInk: "#4A3600",
  marker: "#0B6B3A", // highway mile-marker green
  paper: "#FFFFFF",
  mist: "#F2F4F7",
  line: "#D5DBE3",
  ink: "#16202C",
  muted: "#5B6776",
  faint: "#9AA5B3",
  danger: "#B42318",
  head: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
  body: "'Barlow', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

export const CATEGORIES = [
  {
    value: "insurance",
    label: "Insurance",
    partnerLabel: "Insurance Partner",
    blurb: "Specialized insurance solutions for pilot car operators and oversize transportation professionals.",
  },
  {
    value: "equipment",
    label: "Pilot car equipment",
    partnerLabel: "Equipment Partner",
    blurb: "Signs, lighting, flags, and safety gear for escort vehicles and oversize loads.",
  },
  {
    value: "training",
    label: "Training and certification",
    partnerLabel: "Training Partner",
    blurb: "Courses and certification programs for pilot car operators.",
  },
  {
    value: "permits",
    label: "Permits and compliance",
    partnerLabel: "Permits and Compliance Partner",
    blurb: "Permit services and compliance support for oversize and overweight moves.",
  },
  {
    value: "transportation",
    label: "Transportation services",
    partnerLabel: "Transportation Partner",
    blurb: "Transportation services that support carriers and oversize load operations.",
  },
  {
    value: "other",
    label: "Other industry resources",
    partnerLabel: "Industry Partner",
    blurb: "Resources and services for oversize transportation professionals.",
  },
];

export const getCategory = (value) => CATEGORIES.find((c) => c.value === value) || CATEGORIES[0];

// Placeholder shown before a visitor uploads their own logo
const SAMPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 80">
  <rect x="2" y="2" width="236" height="76" rx="10" fill="none" stroke="#9AA5B3" stroke-width="2.5" stroke-dasharray="7 6"/>
  <text x="120" y="49" text-anchor="middle" font-family="Arial, sans-serif" font-weight="700"
        font-size="22" letter-spacing="3" fill="#9AA5B3">YOUR LOGO</text>
</svg>`;
export const SAMPLE_LOGO = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SAMPLE_SVG)}`;

export function useBrandFonts() {
  useEffect(() => {
    const id = "pl-barlow-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600&family=Barlow+Condensed:wght@600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

// ---------- Partner card ----------
export function PartnerCard({ logoUrl, name, category, description, website, linkUrl, preview = false }) {
  const cat = getCategory(category);
  const text = (description || "").trim() || cat.blurb;
  const href = linkUrl || website;

  return (
    <article
      className="flex flex-col rounded-lg overflow-hidden h-full"
      style={{
        background: T.paper,
        border: `1px solid ${T.line}`,
        boxShadow: "0 1px 2px rgba(0,35,73,0.08)",
        fontFamily: T.body,
      }}
    >
      <div className="flex items-center justify-center px-5" style={{ height: 120, background: T.mist }}>
        <img
          src={logoUrl || SAMPLE_LOGO}
          alt={`${name || "Company"} logo`}
          style={{ maxHeight: 84, maxWidth: "85%", objectFit: "contain" }}
        />
      </div>
      <div className="flex flex-col gap-1.5 p-5 flex-1">
        <h3 style={{ fontFamily: T.head, fontWeight: 700, fontSize: 22, color: T.ink, lineHeight: 1.15 }}>
          {name || "Your Company"}
        </h3>
        <p style={{ color: T.marker, fontWeight: 600, fontSize: 14 }}>{cat.partnerLabel}</p>
        <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.5 }}>{text}</p>
        <div className="mt-auto pt-2">
          {preview || !href ? (
            <span style={{ color: T.navy, fontWeight: 600, fontSize: 15 }}>Learn more</span>
          ) : (
            <a
              href={href}
              target="_blank"
              rel="noopener sponsored"
              className="pl-focus"
              style={{ color: T.navy, fontWeight: 600, fontSize: 15, textDecoration: "none" }}
            >
              Learn more
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export function PartnerGrid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">{children}</div>;
}

// ---------- Live section for pilotlinkusa.org/partners ----------
export function PartnerNetworkSection({ apiUrl = "/api/partners/" }) {
  useBrandFonts();
  const [partners, setPartners] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    fetch(apiUrl, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => {
        if (!alive) return;
        setPartners(Array.isArray(data) ? data : data.results || []);
        setStatus("ready");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
    };
  }, [apiUrl]);

  const shown = filter === "all" ? partners : partners.filter((p) => p.category === filter);

  return (
    <section style={{ fontFamily: T.body, color: T.ink }}>
      <h2 style={{ fontFamily: T.head, fontWeight: 700, fontSize: 30, color: T.navy }}>Partner Network</h2>
      <p style={{ color: T.muted, fontSize: 16, marginTop: 2 }}>
        Companies serving pilot car operators, carriers, and dispatchers.
      </p>

      <div className="flex flex-wrap gap-2 mt-4">
        {[{ value: "all", label: "All" }, ...CATEGORIES].map((c) => {
          const on = filter === c.value;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setFilter(c.value)}
              aria-pressed={on}
              className="pl-focus rounded-full px-3 py-1"
              style={{
                fontSize: 13,
                background: on ? T.navy : T.paper,
                color: on ? T.paper : T.muted,
                border: `1px solid ${on ? T.navy : T.line}`,
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {status === "loading" && <p style={{ color: T.muted }}>Loading partners…</p>}
        {status === "error" && <p style={{ color: T.danger }}>Partners couldn't be loaded. Refresh the page to try again.</p>}
        {status === "ready" && shown.length === 0 && (
          <p style={{ color: T.muted }}>No partners listed in this category yet.</p>
        )}
        {status === "ready" && shown.length > 0 && (
          <PartnerGrid>
            {shown.map((p) => (
              <PartnerCard
                key={p.id}
                logoUrl={p.logo_url}
                name={p.company_name}
                category={p.category}
                description={p.description}
                website={p.website}
                linkUrl={p.link_url}
              />
            ))}
          </PartnerGrid>
        )}
      </div>
    </section>
  );
}
